import { ISymbol } from '@cbridge-packages/symbol';
import { IApiErrorParams } from '@cbridge-packages/api';
import { NetworkStatus } from '@cbridge-utils/network';
import { IOption } from '@cbridge-utils/options';
import { ISymbolListResponseCounters } from '@cbridge-utils/datagrid';
import { MakeOptional } from '@cbridge-utils/types';
import { INode } from '@cbridge-packages/node';
import { IServer, ServerType } from '@cbridge-packages/server';
import { IStream, IStreamBase } from '@cbridge-packages/stream';
import { IScheduleFullView } from '@cbridge-packages/schedule';
import { IMarkupDetails, MarkupType } from '@cbridge-packages/markup';
import { IMarkupProfile } from '@cbridge-packages/markup-profile';
import { IClient } from '@cbridge-packages/client';
import { PricingMode, AggregationMethod } from '@cbridge-packages/pricing-profile';
import { ISymbolLp } from '@cbridge-packages/symbol-lp';
import { IAdjustment } from '@cbridge-packages/adjustment-profile';
import { IOrderLifetime } from '@cbridge-packages/order-lifetime';
import { IPriceFiltering } from '@cbridge-packages/price-filtering-profile';
import { ISpreadProfile } from '@cbridge-packages/spread-profile';

export interface IServerSymbolIdentifier {
  serverId: IServer['serverId'];
  name: ISymbolClient['clientSymbolName'];
}

export interface INodeSymbolIdentifier {
  nodeId: INode['nodeId'];
  name: ISymbolClient['clientSymbolName'];
}

export interface ISymbolClient {
  coreSymbol: Partial<ISymbol>;
  clientSymbolName: string;
  stream: IStreamBase;
  scheduleName?: string;
}

export interface ITradableSymbolClient extends ISymbolClient {
  isTradable?: boolean;
  /**
   * ThrottlingInterval per server, in milliseconds
   */
  throttlingIntervalServer?: number;
  /**
   * ThrottlingInterval per core symbol, in milliseconds
   */
  throttlingIntervalCoreSymbol?: number;
}

export interface ITradableSymbolClientExtraProps {
  adjustmentProfile: ISymbolClientAdjustmentProfile;
  lpMultipliers?: {
    price?: number;
    volume?: number;
  };
}

export interface ISymbolClientWithPricing extends ITradableSymbolClient, ITradableSymbolClientExtraProps {
  markup?: ISymbolClientMarkup;
  spread?: {
    tobMinSpread: number | null;
    tobMaxSpread: number | null;
  };
  priceFilteringProfile: ISymbolClientPriceFilteringProfile;
  lpMarkups?: {
    bid?: number;
    ask?: number;
    type?: MarkupType;
  };
}

export enum ExecutionType {
  BookA = 'aBook',
  BookB = 'bBook',
  Reject = 'reject',
}

export interface ISymbolClientWithRouting extends ITradableSymbolClient, ITradableSymbolClientExtraProps {
  executionType: ExecutionType;
  pricingMode?: PricingMode;
  lpVolumeValidation?: {
    min: number;
    max: number;
    step: number;
  };
}

export type ISymbolClientInServer = ITradableSymbolClient | ISymbolClientWithPricing | ISymbolClientWithRouting;

export interface ISymbolClientClientDetails extends Omit<IClient, 'name' | 'type'> {
}

export interface ISymbolClientServer {
  serverId: IServer['serverId'];
  serverName: string;
  type: ServerType;
  pricingClient?: ISymbolClientClientDetails;
  tradingClient?: ISymbolClientClientDetails;
  /**
   * in milliseconds
   */
  throttlingInterval?: number;
}

export interface ISymbolClientMarkup extends Omit<IMarkupDetails, 'symbolId' | 'coreSymbolName'> {
  profile: IMarkupProfile;
}

export interface ISymbolClientLp {
  id: number;
  name: string;
  lpSymbol?: Omit<ISymbolLp, 'coreSymbolId' | 'coreSymbolName'>;
}

export interface ISymbolClientLpSymbolMarkup {
  lpName: string;
  lpSymbolName?: string;
  bidMarkup?: number;
  askMarkup?: number;
}

export interface ISymbolClientLpSymbolValidation {
  lpName: string;
  lpSymbolName?: string;
  minVolume?: number;
  maxVolume?: number;
  stepVolume?: number;
}

export type ISymbolClientOrderLifetimeProfile = MakeOptional<IOrderLifetime, 'orderLifetimeId' | 'name'>;

export type ISymbolClientAdjustmentProfile = MakeOptional<IAdjustment, 'adjustmentId' | 'name'>;

export type ISymbolClientPriceFilteringProfile = MakeOptional<IPriceFiltering, 'priceFilteringId' | 'name'>;

export interface ISymbolClientPricingProfile {
  id: number;
  name: string;
  mode: PricingMode;
  aggregationMethod?: AggregationMethod;
}

export interface ISymbolClientRoutingRule {
  executionType: ExecutionType;
  accounts?: string[];
  accountRegexp?: string;
  accountGroups?: string[];
  accountGroupRegexp?: string;
}

export interface ISymbolClientDetails extends ISymbolClient {
  schedule?: IScheduleFullView;
  servers: ISymbolClientServer[];
  lps: ISymbolClientLp[];
  markup?: ISymbolClientMarkup;
  lpSymbolMarkups?: ISymbolClientLpSymbolMarkup[];
  lpSymbolValidations?: ISymbolClientLpSymbolValidation[];
  node: IOption<INode['nodeId']>;
  orderLifetimeProfile?: ISymbolClientOrderLifetimeProfile;
  adjustmentProfile?: ISymbolClientAdjustmentProfile;
  priceFilteringProfile?: ISymbolClientPriceFilteringProfile;
  routingRules?: ISymbolClientRoutingRule[];
  pricingProfile?: ISymbolClientPricingProfile;
  spreadProfile?: ISpreadProfile;
}

export enum ClientSymbolFilter {
  Mapped = 'mapped',
  NonTradable = 'nonTradable', // Only for Get Server Client Symbols
  Tradable = 'tradable', // Only for Get Server Client Symbols
  Unmapped = 'unmapped',
}

export interface ISymbolClientFilter {
  limit?: number;
  skip?: number;
  streamId?: IStream['streamId'];
  symbolGroup?: string;
  search?: string;
  filter?: ClientSymbolFilter;
  sort?: string[];
}

export interface ISymbolClientForm {
  nodeId: INode['nodeId'];
  coreSymbolId: ISymbol['symbolId'];
  streamId: IStreamBase['streamId'];
  name: string;
}

export interface ISymbolClientUpdate {
  nodeId: INode['nodeId'];
  clientSymbolName: string;
  name: string;
  streamId: IStreamBase['streamId'];
}

export enum ClientSymbolCommonColumn {
  BaseAsset = 'baseAsset',
  ClientSymbolName = 'clientSymbolName',
  CoreSymbolName = 'coreSymbolName',
  PriceDigits = 'priceDigits',
  QuoteAsset = 'quoteAsset',
  ScheduleName = 'scheduleName',
  StreamName = 'streamName',
  SymbolGroup = 'symbolGroup',
  VolumeDigits = 'volumeDigits',
}

export enum ClientSymbolExtraColumn {
  ClientAskMarkup = 'clientAskMarkup',
  ClientBidMarkup = 'clientBidMarkup',
  ClientPriceMultiplier = 'clientPriceMultiplier',
  ClientVolumeMultiplier = 'clientVolumeMultiplier',
  ExecutionType = 'executionType',
  LpAskMarkup = 'lpAskMarkup',
  LpBidMarkup = 'lpBidMarkup',
  LpMaxVolume = 'lpMaxVolume',
  LpMinVolume = 'lpMinVolume',
  LpPriceMultiplier = 'lpPriceMultiplier',
  LpStepVolume = 'lpStepVolume',
  LpVolumeMultiplier = 'lpVolumeMultiplier',
  MaxBookbMarketDepth = 'maxBookbMarketDepth',
  MaxClientMarketDepth = 'maxClientMarketDepth',
  PricingMode = 'pricingMode',
  PricingTimeout = 'pricingTimeout',
  TobMinSpread = 'tobMinSpread',
  TobMaxSpread = 'tobMaxSpread',
}

export interface ISymbolClientNodeKey extends ISymbolClientFilter {
  nodeId: INode['nodeId'];
}

export interface ISymbolClientServerKey extends ISymbolClientFilter {
  serverId: IServer['serverId'];
}

export interface ISymbolClientList extends ISymbolListResponseCounters {
  items: ISymbolClient[];
}

export interface ISymbolClientStoreData extends ISymbolClientDetails {
  networkStatus?: NetworkStatus;
}

export interface ISymbolClientStore {
  data: Record<string/* Composite ID: Server/Node ID + Symbol name */, ISymbolClientStoreData>;
  counters: Record<string/* Serialized node ID or server ID and filters */, ISymbolListResponseCounters>;
  listNetworkStatus: NetworkStatus;
  listError?: IApiErrorParams;
}

export interface ISymbolClientStoreSegment {
  symbolClient: ISymbolClientStore;
}
